import { useEffect } from 'react';
import Header from '../../../components/base/Header';
import Footer from '../../../components/base/Footer';
import { Link } from 'react-router-dom';

export default function NaturalistVisitBlog() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      <Header />

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-r from-teal-500 to-emerald-500">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <i className="ri-plant-line text-5xl text-white"></i>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              🌿 花屋めぐりシリーズ【第2弾】The Naturalist に行ってきた
            </h1>
            <p className="text-xl text-white/90 mb-6">
              ──植物と静かに向き合える空間──
            </p>
            <div className="flex items-center justify-center gap-4 text-white/90">
              <span className="flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                2026年12月7日
              </span>
              <span className="flex items-center">
                <i className="ri-user-line mr-2"></i>
                ももちゃん
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/308f38f6df35766a6495e648d0c30acd.png"
                alt="The Naturalistの店内"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                こんにちは！ももちゃんです🌸
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                休日、家族で上尾市にある花屋<br />
                <strong className="text-teal-700">The Naturalist（ザ・ナチュラリスト）</strong>に立ち寄りました。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                にぎやかな花屋、というよりも<br />
                「植物と静かに向き合える空間」という印象。
              </p>
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-6 my-6 text-center border-l-4 border-teal-400">
                <p className="text-teal-800 font-bold text-xl">
                  🌿 今回は、特に心に残った<br />
                  エアープランツ（チランジア）のコーナーを中心にご紹介します
                </p>
              </div>
            </div>

            {/* Section 1 */}
            <div className="mb-12 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-emerald-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🚪</span>
                店内に入って、最初に感じたこと
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  扉を開けた瞬間、<br />
                  まず目に入ったのは立体的な空間の使い方でした。
                </p>
                <p className="font-medium text-emerald-800">
                  壁、天井、棚。<br />
                  植物が「並べられている」のではなく、<br />
                  自然に"在っていい場所"に配置されている。
                </p>
                <div className="bg-white rounded-2xl p-6 my-6 border-l-4 border-emerald-400">
                  <p className="text-emerald-700">
                    特にエアープランツは、<br />
                    吊るす・絡める・置く、という自由な展示で、<br />
                    見ているだけでも楽しい構成になっていました。
                  </p>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-12 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-teal-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌱</span>
                エアープランツが主役になる理由
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  エアープランツは、<br />
                  土がなくても生きていける植物です。
                </p>
                
                <div className="grid md:grid-cols-3 gap-4 my-6">
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-teal-400">
                    <h3 className="text-lg font-bold text-teal-700 mb-2 flex items-center">
                      💧 水やりは霧吹きで十分
                    </h3>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-cyan-400">
                    <h3 className="text-lg font-bold text-cyan-700 mb-2 flex items-center">
                      📍 置き場所も比較的自由
                    </h3>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-emerald-400">
                    <h3 className="text-lg font-bold text-emerald-700 mb-2 flex items-center">
                      ✨ 構えすぎなくていい
                    </h3>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-teal-100 to-emerald-100 rounded-2xl p-6 my-6">
                  <p className="text-teal-800 font-medium text-lg">
                    The Naturalistの展示を見ていると、<br />
                    エアープランツは<br />
                    <strong>"育てる植物"というより、"一緒に過ごす存在"</strong><br />
                    なのだと感じました。
                  </p>
                </div>

                <p>
                  完璧な管理を求められない。<br />
                  でも、放っておくと少し拗ねる。<br />
                  その距離感が、どこか人間らしい。
                </p>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-green-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">👨‍👩‍👦</span>
                家族で見ていて気づいたこと
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  家族で見ていると、<br />
                  それぞれ目を止める植物が違うのも面白いところです。
                </p>
                
                <div className="bg-white rounded-2xl p-6 my-6 border-l-4 border-green-400">
                  <div className="space-y-2 text-green-700">
                    <p>「これ、かっこいいね」</p>
                    <p>「この形、変わってる」</p>
                    <p>「ここに飾ったらどうかな」</p>
                  </div>
                </div>

                <p className="font-medium text-green-800">
                  正解がないからこそ、<br />
                  会話が自然に生まれる。
                </p>

                <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-2xl p-6 my-6">
                  <p className="text-green-800 font-medium">
                    エアープランツは、<br />
                    植物好きじゃなくても会話のきっかけになる<br />
                    そんな存在だと思います。
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="mb-12 bg-gradient-to-br from-cyan-50 to-teal-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-cyan-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">💡</span>
                「育てる」より「置いてみる」から始めてもいい
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  The Naturalistのエアープランツを見て、<br />
                  強く感じたのはこれでした。
                </p>
                
                <div className="bg-white rounded-2xl p-8 my-6 text-center border-2 border-cyan-200">
                  <p className="text-2xl font-bold text-cyan-800 mb-4">
                    植物は、<br />
                    最初からうまく育てなくてもいい。
                  </p>
                  <p className="text-lg text-cyan-700">
                    まずは、<br />
                    気に入ったものを一つ、置いてみる。<br />
                    そこから関係が始まる。
                  </p>
                </div>

                <p className="font-medium text-cyan-800">
                  完璧じゃなくていい。<br />
                  その考え方が、<br />
                  このお店全体の空気に流れているように感じました。
                </p>
              </div>
            </div>

            {/* Momochan's Comment */}
            <div className="mb-12 bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-pink-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌸</span>
                モモちゃん畑的・ひとこと
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <div className="bg-white rounded-2xl p-8 my-6 text-center border-2 border-pink-200">
                  <p className="text-xl font-medium text-pink-800 mb-4">
                    エアープランツは、<br />
                    「がんばらなくても、ちゃんとそこにいていい」<br />
                    と教えてくれる植物なのかもしれません。
                  </p>
                  <p className="text-lg text-pink-700">
                    The Naturalistは、<br />
                    そんな感覚を思い出させてくれる花屋でした。
                  </p>
                </div>
              </div>
            </div>

            {/* Store Information */}
            <div className="mb-12 bg-gradient-to-br from-gray-50 to-slate-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">📍</span>
                店舗情報
              </h2>
              <div className="space-y-4 text-gray-700">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <span className="font-bold text-gray-800 w-20">店名：</span>
                      <span>The Naturalist（ザ・ナチュラリスト）</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-bold text-gray-800 w-20">住所：</span>
                      <span>埼玉県上尾市</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <span className="font-bold text-gray-800 w-20">特徴：</span>
                      <span>エアープランツ充実 🌿</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-bold text-gray-800 w-20">雰囲気：</span>
                      <span>静かで落ち着いた空間</span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">
                    <i className="ri-information-line mr-2"></i>
                    営業時間や定休日は変更される場合があります。お出かけ前に公式サイトでご確認ください。
                  </p>
                </div>
              </div>
            </div>

            {/* Closing Message */}
            <div className="mb-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 text-center">
              <h2 className="text-3xl font-bold text-green-800 mb-6">
                🌼 花屋めぐりシリーズ、引き続き更新中！
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                家族と一緒に、これからもたくさんの花屋さんを巡っていきます🚶‍♀️<br />
                それぞれのお店に、それぞれの魅力がある✨
              </p>
              <p className="text-lg font-bold text-green-800">
                次はどこの花屋さんに行こうかな？お楽しみに😊
              </p>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-12">
              <span className="px-4 py-2 bg-teal-100 text-teal-700 text-sm rounded-full">
                #花屋めぐり
              </span>
              <span className="px-4 py-2 bg-teal-100 text-teal-700 text-sm rounded-full">
                #TheNaturalist
              </span>
              <span className="px-4 py-2 bg-teal-100 text-teal-700 text-sm rounded-full">
                #エアープランツ
              </span>
              <span className="px-4 py-2 bg-teal-100 text-teal-700 text-sm rounded-full">
                #チランジア
              </span>
              <span className="px-4 py-2 bg-teal-100 text-teal-700 text-sm rounded-full">
                #上尾市
              </span>
            </div>

            {/* Back Button */}
            <div className="text-center">
              <Link
                to="/essay"
                className="inline-flex items-center bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                エッセイ一覧に戻る
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-green-800 mb-8 text-center">
              他のブログ記事も読む
            </h2>
            <div className="text-center">
              <Link
                to="/essay"
                className="inline-flex items-center text-green-600 hover:text-green-700 font-medium transition-colors"
              >
                すべての記事を見る
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}